// FILE: /src/Components/PostUploader/CaptionGenerator.js (FULLY UPDATED)

import { Modal, Input, Button, Spin, Select, Checkbox, Typography, Image as AntImage, Flex, message } from "antd";
import { useState, useEffect } from "react";
import { generateCaptionService } from "../../services/ai";
import UpgradeModal from '../BuySubscription/UpgradeModal';
import axiosServices from "../../services/axios";
import { uploadSingleImage } from "../../services/user.profile";
import { useSelector } from "react-redux";

const { Title, Text } = Typography;

const fetchCaptionVariants = async (payload) => {
    try {
        const response = await axiosServices.post('/api/caption-variants', payload);
        return response.data.variants || [];
    } catch (error) {
        console.error("Error fetching caption variants:", error);
        message.error("Could not generate caption variants.");
        return [];
    }
};

export default function CaptionGenerator({ setMessage, mediaItems = [], isOpen, onClose }) {
    const { selectedGroup, groups } = useSelector((state) => state.groups);
    const [isLoading, setIsLoading] = useState(false);
    const [isVariantLoading, setIsVariantLoading] = useState(false);
    const [generatedCaptions, setGeneratedCaptions] = useState([]);
    const [selectedMediaUrls, setSelectedMediaUrls] = useState([]);
    const [upgradeModal, setUpgradeModal] = useState({ visible: false, message: '', featureName: '' });

    useEffect(() => {
        if (isOpen && mediaItems.length > 0) {
            const firstPermanentUrl = mediaItems[0]?.permanentUrl || mediaItems[0]?.localUrl;
            if (firstPermanentUrl) {
                setSelectedMediaUrls([firstPermanentUrl]);
            }
        } else {
            setSelectedMediaUrls([]);
        }
        setGeneratedCaptions([]);
    }, [mediaItems, isOpen]);

    const [captionData, setCaptionData] = useState({
        instruction: "Make this caption emotionally inspiring for creatives",
        platform: ["Instagram"],
        tone: ["Inspirational"],
        emoji_density: "medium",
        caption_length: "long",
        keywords: "creativity, art, freedom",
        generate_hashtags: true,
        n: 3,
    });
    
    const handleFieldChange = (key, value) => {
        setCaptionData(prev => ({ ...prev, [key]: value }));
    };

    const handleMediaSelect = (url) => {
        setSelectedMediaUrls(prev => 
            prev.includes(url) ? prev.filter(item => item !== url) : [...prev, url]
        );
    };

    const handleGenerateInitialCaptions = async () => {
        if (selectedMediaUrls.length === 0) {
            message.warning("Please select at least one image to generate a caption.");
            return;
        }

        const groupId = groups.find(g => g.name === selectedGroup)?._id;
        if (!groupId) {
            message.error("A group must be selected to process media.");
            return;
        }

        setIsLoading(true);
        setGeneratedCaptions([]);

        try {
            const itemsToUpload = mediaItems.filter(item => selectedMediaUrls.includes(item.localUrl) && !item.permanentUrl);
            
            const uploadPromises = itemsToUpload.map(item => uploadSingleImage(item.file, groupId));
            const uploadResults = await Promise.all(uploadPromises);

            const urlMap = itemsToUpload.reduce((acc, item, index) => {
                acc[item.localUrl] = uploadResults[index].url;
                return acc;
            }, {});

            const finalUrlsForAI = selectedMediaUrls.map(url => urlMap[url] || url);

            const payload = {
                url: finalUrlsForAI,
                ...captionData,
                keywords: captionData.keywords.split(',').map(k => k.trim()),
            };

            const response = await generateCaptionService(payload);
            setGeneratedCaptions(response || []);

        } catch (err) {
            if (err.isLimitError) {
                setUpgradeModal({ visible: true, message: err.message, featureName: err.feature });
            } else {
                message.error("Failed to generate captions. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleVariantAction = async (originalCaption, actionType) => {
        setIsVariantLoading(true);
        const payload = {
            caption: originalCaption,
            expand: actionType === 'expand',
            generate_similar: actionType === 'rewrite',
            n: 1
        };
        const variants = await fetchCaptionVariants(payload);
        setGeneratedCaptions(prev => [...prev, ...variants.map(v => ({ caption: v.text, type: v.type }))]);
        setIsVariantLoading(false);
    };

    const handleInsertCaption = (captionToInsert) => {
        setMessage(prev => prev ? `${prev}\n\n${captionToInsert}` : captionToInsert);
        message.success("Caption inserted!");
    };
    
    return (
        <>
            <Modal
                title={
                    <Title level={4} className="flex items-center gap-2 m-0">
                        <i className="fa-solid fa-wand-magic-sparkles text-yellow-500"></i>
                        Generate Captivating Captions
                    </Title>
                }
                open={isOpen}
                onCancel={onClose}
                footer={[ <Button key="back" onClick={onClose}>Close</Button> ]}
                width={800}
                destroyOnClose
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 p-2">
                    <div className="space-y-4">
                        <FormItem label="Select Media" icon="fa-camera">
                            <div className="p-2 border rounded-lg bg-gray-50 min-h-[96px] max-h-48 overflow-y-auto">
                                <div className="flex flex-wrap gap-2">
                                    {mediaItems.map((item, index) => {
                                        const url = item.permanentUrl || item.localUrl;
                                        return (
                                            <div key={index} className="relative cursor-pointer" onClick={() => handleMediaSelect(url)}>
                                                <AntImage src={url} alt={`Media ${index}`} height={70} width={70} className={`rounded-md object-cover border-4 ${selectedMediaUrls.includes(url) ? 'border-blue-500' : 'border-transparent'}`} preview={false} />
                                                {selectedMediaUrls.includes(url) && <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-md"><i className="fa-solid fa-check-circle text-white text-2xl"></i></div>}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </FormItem>
                        <FormItem label="Instruction (Optional)" icon="fa-list-check">
                            <Input placeholder="e.g., Make it witty and add a question" value={captionData.instruction} onChange={(e) => handleFieldChange('instruction', e.target.value)} />
                        </FormItem>
                        
                        <Flex gap="middle">
                            <FormItem label="Platform" icon="fa-share-nodes">
                                <Select mode="multiple" allowClear placeholder="Select platforms" value={captionData.platform} className="w-full" onChange={(v) => handleFieldChange('platform', v)}>
                                    <Select.Option value="Instagram">Instagram</Select.Option>
                                    <Select.Option value="Facebook">Facebook</Select.Option>
                                    <Select.Option value="LinkedIn">LinkedIn</Select.Option>
                                </Select>
                            </FormItem>
                            <FormItem label="Tone" icon="fa-smile">
                                 <Select mode="multiple" allowClear placeholder="Select tones" value={captionData.tone} className="w-full" onChange={(v) => handleFieldChange('tone', v)}>
                                    <Select.Option value="Inspirational">Inspirational</Select.Option>
                                    <Select.Option value="Funny">Funny</Select.Option>
                                    <Select.Option value="Professional">Professional</Select.Option>
                                </Select>
                            </FormItem>
                        </Flex>
                        <Flex gap="middle">
                            <FormItem label="Emoji Density" icon="fa-face-grin-beam">
                                <Select value={captionData.emoji_density} className="w-full" onChange={(v) => handleFieldChange('emoji_density', v)}>
                                    <Select.Option value="low">Low</Select.Option>
                                    <Select.Option value="medium">Medium</Select.Option>
                                    <Select.Option value="high">High</Select.Option>
                                </Select>
                            </FormItem>
                            <FormItem label="Caption Length" icon="fa-align-left">
                                <Select value={captionData.caption_length} className="w-full" onChange={(v) => handleFieldChange('caption_length', v)}>
                                    <Select.Option value="short">Short</Select.Option>
                                    <Select.Option value="medium">Medium</Select.Option>
                                    <Select.Option value="long">Long</Select.Option>
                                </Select>
                            </FormItem>
                        </Flex>
                        <FormItem label="Keywords (Optional)" icon="fa-key">
                            <Input placeholder="e.g., innovation, tech" value={captionData.keywords} onChange={(e) => handleFieldChange('keywords', e.target.value)} />
                        </FormItem>
                        <Checkbox checked={captionData.generate_hashtags} onChange={(e) => handleFieldChange('generate_hashtags', e.target.checked)}>Generate Hashtags</Checkbox>
                        <Button type="primary" block loading={isLoading} onClick={handleGenerateInitialCaptions} size="large">Generate {captionData.n} Captions</Button>
                    </div>
                    <div className="space-y-4">
                        <Title level={5}>Generated Captions</Title>
                        <div className="p-2 border rounded-lg bg-gray-50 min-h-[300px] max-h-[450px] overflow-y-auto space-y-3">
                            {isLoading ? (
                                <div className="text-center py-10"><Spin tip="Generating..." /></div>
                            ) : generatedCaptions.length === 0 ? (
                                <Text type="secondary" className="text-center block py-10">Your generated captions will appear here.</Text>
                            ) : (
                                generatedCaptions.map((item, index) => (
                                    <div key={index} className="p-3 bg-white rounded-md shadow-sm border">
                                        <Text className="block text-sm whitespace-pre-wrap">{item.caption}</Text>
                                        <div className="flex justify-end gap-2 mt-2 pt-2 border-t">
                                            <Button size="small" onClick={() => handleVariantAction(item.caption, 'rewrite')} loading={isVariantLoading}>Rewrite</Button>
                                            <Button size="small" onClick={() => handleVariantAction(item.caption, 'expand')} loading={isVariantLoading}>Expand</Button>
                                            <Button size="small" type="primary" ghost onClick={() => handleInsertCaption(item.caption)}>Insert</Button>
                                        </div>
                                    </div>
                                ))
                            )}
                            {isVariantLoading && <div className="text-center py-2"><Spin size="small" /></div>}
                        </div>
                    </div>
                </div>
            </Modal>

            <UpgradeModal
                isOpen={upgradeModal.visible}
                onClose={() => setUpgradeModal({ visible: false, message: '', featureName: '' })}
                message={upgradeModal.message}
                featureName={upgradeModal.featureName}
            />
        </>
    );
}

const FormItem = ({ label, icon, children }) => (
    <div>
        <label className="flex items-center gap-2 mb-1 font-semibold text-gray-700 text-sm">
            <i className={`fa-solid ${icon} text-gray-500`}></i>
            {label}
        </label>
        {children}
    </div>
);